import { DateTime } from 'luxon';
import { AVAILABILITY_STATUSES } from '../constants';

class SessionHelper {
  static isWithinFilter(isCategoryFilter, session, scheduledSession, filter, activityList) {
    const isInTimeOfDayFilter = this.isInTimeOfDayFilter(scheduledSession, filter);
    const isInSportActivityLocationFilter = this.isInSportActivityLocationFilter(
      scheduledSession,
      filter
    );
    if (isCategoryFilter) {
      return (
        isInTimeOfDayFilter &&
        this.isInCategoryFilter(session, filter) &&
        isInSportActivityLocationFilter
      );
    } else {
      return (
        isInTimeOfDayFilter &&
        this.isInActivityFilter(session, filter, activityList) &&
        isInSportActivityLocationFilter
      );
    }
  }

  static isInCategoryFilter(session, filter) {
    if (filter.activity.length === 0) {
      return true;
    } else if (!(session.subEvent instanceof Array) && session.subEvent.length === 0) {
      return false;
    } else if (
      session.subEvent[0].category instanceof Array &&
      session.subEvent[0].category.length
    ) {
      const categoryFilter = filter.activity;
      const categories = session.subEvent[0].category;

      for (const category of categories) {
        if (categoryFilter.includes(category)) {
          return true;
        }
      }
    }
    return false;
  }

  static hasTimeOfDayFilter(filter) {
    if (typeof filter.timeOfDay === 'undefined' || filter.timeOfDay === 'all') {
      return false;
    }
    switch (filter.timeOfDay) {
      default:
        return false;
      case 'morning':
      case 'afternoon':
      case 'evening':
        return true;
    }
  }

  static isInTimeOfDayFilter(subEvent, filter) {
    const startTime = DateTime.fromISO(subEvent.startDate);
    return this.isHourInTimeOfDayFilter(startTime.hour, filter);
  }

  static isHourInTimeOfDayFilter(startHour, filter) {
    if (typeof filter.timeOfDay === 'undefined' || filter.timeOfDay === 'all') {
      return true;
    } else {
      switch (filter.timeOfDay) {
        default:
          return true;
        case 'morning':
          if (startHour < 12) {
            return true;
          }
          break;
        case 'afternoon':
          if (startHour >= 12 && startHour < 17) {
            return true;
          }
          break;
        case 'evening':
          if (startHour >= 17) {
            return true;
          }
          break;
      }
    }
    return false;
  }

  /**
   * @param {any} session
   * @param {import('../Timetable').TimetableFilter} filter
   * @param {any} activityList
   */
  static isInActivityFilter(session, filter, activityList) {
    if (
      typeof filter.activity === 'undefined' ||
      !(filter.activity instanceof Array) ||
      filter.activity.length === 0 ||
      (filter.activity.length === 1 && !filter.activity[0])
    ) {
      return true;
    } else if (session.activity instanceof Array && session.activity.length) {
      // If the activity filter is Yoga we don't want to filter out Hot Yoga ie we want to keep children of filter
      const activityFilterConcepts = filter.activity
        .map((activityLabel) => activityList.getConceptByLabel(activityLabel))
        .filter(Boolean);
      const childActivitiesOfFilter = activityFilterConcepts.flatMap((concept) =>
        concept.getNarrowerTransitive().map((childConcept) => childConcept.toString())
      );
      const activityWithChildren = [...filter.activity, ...childActivitiesOfFilter];
      for (const activity of session.activity) {
        if (activity.prefLabel) {
          if (activityWithChildren.indexOf(activity.prefLabel) >= 0) {
            return true;
          }
        }
      }
    }
    return false;
  }

  static getMinimumHour(items) {
    let currentHour = 23;
    for (const eventSeries of items) {
      for (const sessionSeries of eventSeries.subEvent) {
        for (const subEvent of sessionSeries.subEvent || []) {
          const startTime = DateTime.fromISO(subEvent.startDate);
          if (startTime.hour < currentHour) {
            currentHour = startTime.hour;
          }
        }
      }
    }
    return currentHour;
  }

  /**
   * @param {string} checkoutBaseUrl
   * @param {string} checkoutUrlTemplate
   * @param {string | undefined} theme
   */
  static checkoutUrl(checkoutBaseUrl, checkoutUrlTemplate, theme) {
    let url = checkoutUrlTemplate.replace(/\{checkoutBaseUrl\}/, checkoutBaseUrl);
    if (theme) {
      url = `${url}?theme=${theme}`;
    }
    return url;
  }

  static description(description) {
    if (description === null || description === undefined) {
      return '';
    }
    return description.replace(/^\s*[0-9]{2}:[0-9]{2}(\s*-\s*[0-9]{2}:[0-9]{2})?([:,;\s*])?/, '');
  }

  static availabilityString(
    sessionAvailability,
    remainingAttendeeCapacity,
    maximumAttendeeCapacity,
    shouldShowExactAvailability
  ) {
    if (shouldShowExactAvailability) {
      return `${remainingAttendeeCapacity}/${maximumAttendeeCapacity} available`;
    }
    switch (sessionAvailability) {
      case AVAILABILITY_STATUSES.AVAILABLE:
        return 'Good availability';
      case AVAILABILITY_STATUSES.LIMITED:
        return 'Limited availability';
      case AVAILABILITY_STATUSES.NONE:
        return 'Full';
      default:
        return null;
    }
  }

  static sessionBookSpaceObject({
    subEvent,
    baseCheckoutUrl,
    isBranded,
    checkoutTheme,
    bookableProviders = [],
    provider,
    shouldShowExactAvailability = false,
  }) {
    const checkoutUrlTemplate = subEvent['imin:checkoutUrlTemplate'];
    let sessionAvailability;

    if (subEvent.remainingAttendeeCapacity < 5) {
      sessionAvailability = AVAILABILITY_STATUSES.LIMITED;
    } else if (subEvent.remainingAttendeeCapacity > 0) {
      sessionAvailability = AVAILABILITY_STATUSES.AVAILABLE;
    } else {
      sessionAvailability = AVAILABILITY_STATUSES.NONE;
    }

    if (!checkoutUrlTemplate && bookableProviders.includes(provider)) {
      return {
        tag: 'span',
        className: 'unavailable',
        text: 'Unavailable to book',
      };
    }

    let sessionBookSpace;

    if (subEvent.startDate < new Date().toISOString()) {
      return {
        tag: 'span',
        className: 'inProgress',
        text: 'In Progress',
      };
    } else if (sessionAvailability === AVAILABILITY_STATUSES.NONE) {
      return {
        tag: 'span',
        className: 'spacesFull',
        text: this.availabilityString(
          sessionAvailability,
          subEvent.remainingAttendeeCapacity,
          subEvent.maximumAttendeeCapacity,
          shouldShowExactAvailability
        ),
      };
    } else if (
      sessionAvailability === AVAILABILITY_STATUSES.AVAILABLE ||
      sessionAvailability === AVAILABILITY_STATUSES.LIMITED
    ) {
      if (baseCheckoutUrl && checkoutUrlTemplate && bookableProviders.includes(provider)) {
        const checkoutUrl = this.checkoutUrl(baseCheckoutUrl, checkoutUrlTemplate, checkoutTheme);
        return {
          tag: 'a',
          className: `button buttonBook ${isBranded}`,
          text: 'Book',
          attributes: {
            href: checkoutUrl,
            target: '_blank',
            rel: 'noopener noreferrer',
          },
          availability: this.availabilityString(
            sessionAvailability,
            subEvent.remainingAttendeeCapacity,
            subEvent.maximumAttendeeCapacity,
            shouldShowExactAvailability
          ),
        };
      }
      if (!sessionBookSpace) {
        return {
          tag: 'span',
          className: 'spacesAvailable',
          text: this.availabilityString(
            sessionAvailability,
            subEvent.remainingAttendeeCapacity,
            subEvent.maximumAttendeeCapacity,
            shouldShowExactAvailability
          ),
        };
      }
    }
  }

  /**
   * @param {any} scheduledSession
   * @param {import('../Timetable').TimetableFilter} filter
   */
  static isInSportActivityLocationFilter(scheduledSession, filter) {
    if (
      typeof filter.sportActivityLocation === 'undefined' ||
      !(filter.sportActivityLocation instanceof Array) ||
      filter.sportActivityLocation.length === 0
    ) {
      return true;
    }

    const sportActivityLocation = scheduledSession['_imin:sal'];
    if (!(sportActivityLocation instanceof Array) || sportActivityLocation.length === 0) {
      return false;
    }

    for (const location of sportActivityLocation) {
      if (filter.sportActivityLocation.includes(location)) {
        return true;
      }
    }

    return false;
  }
}

export default SessionHelper;
